///
/// EarthBrowser
///
/// Created by Matt Giger on 9/19/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Registry connecting Earth's tile diagnostics to the dev-only
/// window.__ebDebug hooks. Lives apart from debugHooks.ts so Earth (part of
/// the globe-only embed build) doesn't transitively import the redux store
/// that debugHooks needs.
///

let tileStatsProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getTileStats() (development
 * builds only). Called by Earth at construction; read lazily so
 * registration order relative to installDebugHooks doesn't matter.
 */
export function registerTileStatsProvider(fn: () => unknown): void {
	tileStatsProvider = fn
}

export function getTileStatsProvider(): (() => unknown) | undefined {
	return tileStatsProvider
}

let skyStatsProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getSky(): the sky ephemeris
 * (sun/moon positions, phase, sidereal time). Called by Sky at construction.
 */
export function registerSkyStatsProvider(fn: () => unknown): void {
	skyStatsProvider = fn
}

export function getSkyStatsProvider(): (() => unknown) | undefined {
	return skyStatsProvider
}

let cloudDetailProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getCloudDetail(): the cloud
 * overlay's detail region (storm cutout / mosaic window) state. Called by
 * CloudsOverlay at construction.
 */
export function registerCloudDetailProvider(fn: () => unknown): void {
	cloudDetailProvider = fn
}

export function getCloudDetailProvider(): (() => unknown) | undefined {
	return cloudDetailProvider
}

let crosshairStatsProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getCrosshair(): reticle /
 * readout visibility and the camera motion source driving them. Called by
 * EarthActivity at construction.
 */
export function registerCrosshairStatsProvider(fn: () => unknown): void {
	crosshairStatsProvider = fn
}

export function getCrosshairStatsProvider(): (() => unknown) | undefined {
	return crosshairStatsProvider
}

let userLocationStatsProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getUserLocation(): the "you
 * are here" marker's screen state, the active follow mode and the
 * dialog.userLocation slice. Called by EarthActivity at construction.
 */
export function registerUserLocationStatsProvider(fn: () => unknown): void {
	userLocationStatsProvider = fn
}

export function getUserLocationStatsProvider(): (() => unknown) | undefined {
	return userLocationStatsProvider
}

let webcamStatsProvider: (() => unknown) | undefined

/**
 * Register a provider for window.__ebDebug.getWebcams(): the webcam
 * cloud's tier / visible-item / materialized-node state. Called by
 * WebcamsDatasetLayer at construction.
 */
export function registerWebcamStatsProvider(fn: () => unknown): void {
	webcamStatsProvider = fn
}

export function getWebcamStatsProvider(): (() => unknown) | undefined {
	return webcamStatsProvider
}

export interface CityVignetteDebug {
	/** Current state for window.__ebDebug.getCityVignette() */
	snapshot: () => unknown
	/** Replace the sampled conditions (partial CurrentWeather) or clear the override with null */
	override: (weather: Record<string, unknown> | null) => void
}

let cityVignetteDebug: CityVignetteDebug | undefined

/**
 * Register (or clear, with undefined) the focused city's weather vignette
 * for the window.__ebDebug hooks. Set by CityLabelNode while a vignette is
 * attached.
 */
export function registerCityVignetteDebug(debug: CityVignetteDebug | undefined): void {
	cityVignetteDebug = debug
}

export function getCityVignetteDebug(): CityVignetteDebug | undefined {
	return cityVignetteDebug
}
